import { Component, EventEmitter, Input, OnChanges, OnInit, Output, inject } from '@angular/core';
import { FormControl, FormGroup, Validators, ReactiveFormsModule } from '@angular/forms';
import { CommonModule } from '@angular/common';
import { MatCardModule } from '@angular/material/card';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatTabsModule } from '@angular/material/tabs';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatSelectModule } from '@angular/material/select';
import { MatOptionModule } from '@angular/material/core';
import { MatSlideToggleModule } from '@angular/material/slide-toggle';
import { MatSnackBar } from '@angular/material/snack-bar';
import { MatDialog } from '@angular/material/dialog';
import { Observable, map, of, Subscription, lastValueFrom, Subject } from 'rxjs';
import { ActivatedRoute } from '@angular/router';
import { Advertiser, AdvertiserLanguageDto, Campaign, CampaignAdminRequest, CampaignsAdvertiserAdminApiClient, CampaignsCampaignAdminApiClient, CampaignStatus, TimeZoneItem } from 'src/app/api/api-campaigns.service.generated';
import { GUID } from 'src/app/common/utils/GuidUtils';
import { S3Upload } from 'src/app/common/utils/S3Upload';
import { BannerImage } from '../campaign-banner-image/campaign-banner-image.component';
import { Country, CountryCode } from './country-code';
import { MultiLabelsComponent } from 'src/app/common/components/multi-labels/multi-labels.component';
import { PhotoComponent } from 'wecheer-common';
// Quill setup moved to QuillConfigurator helper
import { CampaignDescriptionLinkComponent } from './campaign-description-link/campaign-description-link.component';
import { UploadImageService } from 'src/app/common/services/upload-image.service';
import { MatSlideToggleChange } from '@angular/material/slide-toggle';
import { PERMISSION } from 'src/app/const/permission';
import { environment } from 'src/environments/environment';
import { PunchCardCampaignService } from '../../punch-card-campaign-edit/punch-card-campaign-edit.service';
import { QuillModule } from 'ngx-quill'
import { QuillConfigurator } from 'src/app/common/utils/QuillConfigurator';
import { MonacoEditorModule } from 'ngx-monaco-editor-v2';
import { UserPermissionsService } from 'src/app/common/services/user-permissions.service';
import { getAllLanguages, LanguageItem } from 'src/app/common/utils/language-mapping.utils';
import { CampaignLanguageWarningDialogComponent } from './campaign-language-warning-dialog/campaign-language-warning-dialog.component';
const enum EDITOR_TYPE {
  VISUAL,
  TEXT
}
@Component({
    selector: 'app-campaign-base-edit',
    templateUrl: './campaign-base-edit.component.html',
    styleUrls: ['./campaign-base-edit.component.scss'],
    imports: [
      CommonModule,
      ReactiveFormsModule,
      MatCardModule,
      MatFormFieldModule,
      MatInputModule,
      MatIconModule,
      MatButtonModule,
      MatTooltipModule,
      MatTabsModule,
      MatDatepickerModule,
      MatSelectModule,
      MatOptionModule,
      MatSlideToggleModule,
      QuillModule,
      MonacoEditorModule,
      MultiLabelsComponent,
      PhotoComponent,
    ]
})
export class CampaignBaseEditComponent implements OnInit, OnChanges {
  private campaignsApi = inject(CampaignsCampaignAdminApiClient);
  private advertiserApi = inject(CampaignsAdvertiserAdminApiClient);
  private snackBar = inject(MatSnackBar);
  private dialog = inject(MatDialog);
  private uploadImageService = inject(UploadImageService);
  private userPermissionsService = inject(UserPermissionsService);
  private punchCardCampaignService = inject(PunchCardCampaignService);
  private route = inject(ActivatedRoute);

  @Input() campaign: Campaign;
  @Input() isStaffCampaign: boolean = false;
  @Input() advertiser: Advertiser;
  @Output() mainBannerChanged = new EventEmitter<BannerImage>();
  @Output() isContactSync = new EventEmitter<boolean>();
  @Output() isDataConsumerSync = new EventEmitter<boolean>();

  editModel: CampaignAdminRequest;
  allCountries: Observable<Country[]> = of([]);
  allLanguages: Observable<LanguageItem[]> = of([]);
  filteredLanguages: LanguageItem[] = [];
  availableLanguagesForPrimary: LanguageItem[] = [];
  refreshLanguages = new Subject<string>();
  refreshCountries = new Subject<string>();
  unjoinedDescriptionSelectedType = EDITOR_TYPE.VISUAL;
  joinedDescriptionSelectedType = EDITOR_TYPE.VISUAL;
  unjoinedDescriptionZoomed = false;
  joinedDescriptionZoomed = false;
  isBrandAdmin = false;
  isHandshakeQR = false;

  private subscription: Subscription = new Subscription();

  public modules = QuillConfigurator.getModules();

  public monacoEditorOptions = {
    theme: 'vs',
    language: 'html',
    fontSize: 12,
    lineNumbers: 'off',
    minimap: { enabled: false },
    scrollBeyondLastLine: false,
    wordWrap: 'on',
    formatOnType: true,
    formatOnPaste: true,
    automaticLayout: true,
    padding: { top: 10, bottom: 10 },
    readOnly: false,
    contextmenu: true,
    folding: false,
    glyphMargin: false,
    // Use decoration gutter width as left padding
    lineDecorationsWidth: 12,
    lineNumbersMinChars: 3,
    renderLineHighlight: 'none',
    selectOnLineNumbers: false,
    hideCursorInOverviewRuler: true
  };

  onMonacoInit(editor: any, controlName: 'detailedDescription' | 'detailedDescriptionJoined' | 'detailedDescriptionJoinedForRetailer') {
    try {
      // Seed with basic line breaks if single-line HTML
      const ctrl = this.form.get(controlName);
      const value = (ctrl?.value as string) || '';
      if (value && !/\n/.test(value) && />\s*</.test(value)) {
        const seeded = value.replace(/>\s*</g, '>\n<');
        ctrl?.setValue(seeded, { emitEvent: false });
      }

      // Let editor render, then format and layout
      setTimeout(() => {
        try {
          editor.layout?.();
          const action = editor.getAction && editor.getAction('editor.action.formatDocument');
          if (action?.run) {
            action.run();
          }
        } catch {}
      }, 50);
    } catch {}
  }

  form = new FormGroup({
    title: new FormControl('', Validators.required),
    description: new FormControl('', Validators.required),
    detailedDescription: new FormControl(''),
    detailedDescriptionJoined: new FormControl(''),
    detailedDescriptionJoinedForRetailer: new FormControl(''),
    termAndConditions: new FormControl(null),
    privacyPolicy: new FormControl(null),
    startDate: new FormControl('', Validators.required),
    endDate: new FormControl('', Validators.required),
    timeZoneInfoId: new FormControl(''),
    overAge: new FormControl(false),
    dynamicAge: new FormControl(18),
    shouldAdvertise: new FormControl(''),
    canJoin: new FormControl(''),
    blockRedemptionOnJoin: new FormControl(''),
    isForceUsingMobileApp : new FormControl(''),
    shouldHideParticipatingLocations: new FormControl(''),
    allowDuplicates: new FormControl(''),
    onboardingUrl: new FormControl(),
    allowGiveStamps: new FormControl(''),
    campaignStatus: new FormControl(''),
    isGlobal: new FormControl(true),
    countries: new FormControl(''),
    languages: new FormControl([]),
    primaryLanguage: new FormControl(''),
    synchronizationContactGLO: new FormControl(false),
    synchronizationDataConsumerGLO: new FormControl(false),
    allowedCountryCodes: new FormControl(''),
  });

  photoUrl: string;
  uploadPhotoKey: string;

  photoBannerUrl: string;
  uploadBannerKey: string;

  ALLOWED_EXTENSIONS = ['jpg', 'jpeg', 'png'];
  ALLOWED_EXTENSIONS_BANNER = ['jpg', 'jpeg', 'png', 'gif'];
  campaignStatuses: { name: string, value?: CampaignStatus, index: number }[] = [];
  timeZoneInfos$: Observable<TimeZoneItem[]>;
  filteredCountries: any[] = []

  constructor() {
    this.getCampaignStatuses();
    this.timeZoneInfos$ = this.campaignsApi.getTimeZoneInfos();
  }

  private getCampaignStatuses(): void {
    for (const key in CampaignStatus)
      if (key)
        this.campaignStatuses.push({ name: key, value: CampaignStatus[key] as CampaignStatus, index: Object.keys(CampaignStatus).indexOf(key) + 1 });
  }

  ngOnChanges(): void {
    if (!this.campaign) return;
    const editModel = this.mapEditModelFromCampaign(this.campaign);
    this.setFormValue(editModel);
    this.editModel = editModel;
    this.updateHandshakeQRFlag();

    const onboardingUrl = this.route.snapshot.queryParamMap.get('onboardingUrl');
    if (onboardingUrl && !this.form.get('onboardingUrl')?.value) {
      this.form.patchValue({ onboardingUrl });
    }
  }

  isContactSyncChanged(event: MatSlideToggleChange){
    this.isContactSync.emit(event.checked);
  }

  isDataConsumerSyncChanged(event: MatSlideToggleChange){
    this.isDataConsumerSync.emit(event.checked);
  }

  ngOnInit() {
    QuillConfigurator.setup();
    this.allCountries = of(this.fetchCountryData());
    this.allLanguages = of(getAllLanguages());
    this.userPermissionsService.hasPermission(PERMISSION.BRANDADMIN).subscribe(isBrandAdmin => this.isBrandAdmin = isBrandAdmin);

    // Fetch advertiser if not provided via input
    if (!this.advertiser && this.campaign?.advertiserId) {
      this.advertiserApi.getAdvertiserById(this.campaign.advertiserId).subscribe(adv => {
        this.advertiser = adv;
        this.populateLanguagesFromAdvertiser();
      });
    } else if (this.advertiser) {
      this.populateLanguagesFromAdvertiser();
    }

    // Subscribe to capture tool type changes to update isHandshakeQR
    if (this.punchCardCampaignService) {
      this.subscription.add(
        this.punchCardCampaignService.captureToolTypeChanged.subscribe(captureToolType => {
          this.updateHandshakeQRFlag(captureToolType);
        })
      );
    }
  }

  ngOnDestroy() {
    this.subscription.unsubscribe();
  }

  private updateHandshakeQRFlag(captureToolType?: string) {
    const toolType = captureToolType ||
                    this.campaign?.strategy?.receiptToolTypeId ||
                    this.campaign?.strategy?.captureToolType;

    this.isHandshakeQR = toolType === 'HandshakeQR';
  }

  fetchCountryData(): Country[] {
    return new CountryCode().allCountries.map(c => {
      return {
        productId: c[1].toString(),
        name: c[0].toString(),
        iso2: c[1].toString(),
        dialCode: c[2].toString(),
        priority: +c[3] || 0,
        areaCodes: c[4] as string[] || undefined,
        flagClass: c[1].toString().toLocaleLowerCase(),
        placeHolder: ''
      };
    });
  }

  mapEditModelFromCampaign(campaign: Campaign): CampaignAdminRequest {
    const model = new CampaignAdminRequest({
      campaignId: campaign.id,
      advertiserId: campaign.advertiserId,
      title: campaign.title,
      description: campaign.description,
      detailedDescription: campaign.detailedDescription,
      detailedDescriptionJoined: campaign.detailedDescriptionJoined,
      detailedDescriptionJoinedForRetailer: campaign.detailedDescriptionJoinedForRetailer,
      termAndConditions: campaign.termAndConditions,
      privacyPolicy: campaign.privacyPolicy,
      canJoin: campaign.canJoin,
      blockRedemptionOnJoin: campaign.blockRedemptionOnJoin,
      isForceUsingMobileApp: campaign.isForceUsingMobileApp,
      shouldHideParticipatingLocations: campaign.shouldHideParticipatingLocations,
      startDate: campaign.startDate,
      endDate: campaign.endDate,
      timeZoneInfoId: campaign.timeZoneInfoId,
      photoBannerUrl: campaign.photoBannerUrl,
      photoFullUrl: campaign.photoFullUrl,
      photoThumbUrl: campaign.photoThumbUrl,
      shouldAdvertise: campaign.shouldAdvertise,
      allowDuplicates: campaign.allowDuplicates,
      onboardingUrl: campaign.onboardingUrl,
      allowGiveStamps: campaign.allowGiveStamps,
      overAge: campaign.overAge,
      dynamicAge: campaign.dynamicAge,
      campaignStatus: campaign.status,
      isGlobal: campaign.isGlobal,
      countries: campaign.countries,
      languages: campaign.languages,
      primaryLanguage: campaign.primaryLanguage,
      synchronizationContactGLO: campaign.synchronizationContactGLO,
      synchronizationDataConsumerGLO: campaign.synchronizationDataConsumerGLO,
      allowedCountryCodes: campaign.allowedCountryCodes,
    });
    return model;
  }

  setFormValue(model: CampaignAdminRequest) {
    this.photoBannerUrl = model.photoBannerUrl;
    this.photoUrl = model.photoThumbUrl;
    if (!model.timeZoneInfoId)
      model.timeZoneInfoId = 'UTC';
    const dataPath: any = { ...model }
    dataPath.allowedCountryCodes = model.allowedCountryCodes?.join(', ') ?? '';

    const countryObject = this.allCountries.pipe(
      map((countries) =>
        countries.filter((country) => model.countries?.includes(country.iso2))
      )
    );

    countryObject.subscribe(result => {
      this.filteredCountries = result;
      this.form.patchValue({
        ...dataPath,
        countries: result,
      });
      setTimeout(() => this.refreshCountries.next(''));
    })

    // Set language fields
    const campaignLanguages: LanguageItem[] = (model.languages || []).map(l => ({ code: l.code!, name: l.name! }));
    this.filteredLanguages = campaignLanguages;
    this.availableLanguagesForPrimary = campaignLanguages;
    this.form.patchValue({ languages: campaignLanguages, primaryLanguage: model.primaryLanguage || '' });
    setTimeout(() => this.refreshLanguages.next(''));
  }

  getFormValue() {
    const f: any = this.form.value;

    if (!this.form.valid) {
      this.forceValidatorsToDisplayErrors();
      return null;
    }

    const model = new CampaignAdminRequest({
      title: f.title,
      description: f.description,
      detailedDescription: f.detailedDescription,
      detailedDescriptionJoined: f.detailedDescriptionJoined,
      detailedDescriptionJoinedForRetailer: f.detailedDescriptionJoinedForRetailer,
      endDate: f.endDate,
      startDate: f.startDate,
      timeZoneInfoId: f.timeZoneInfoId,
      termAndConditions: f.termAndConditions,
      privacyPolicy: f.privacyPolicy,
      overAge: f.overAge,
      dynamicAge: f.dynamicAge,
      shouldAdvertise: !!f.shouldAdvertise,
      canJoin: !!f.canJoin,
      blockRedemptionOnJoin: !!f.blockRedemptionOnJoin,
      isForceUsingMobileApp: !!f.isForceUsingMobileApp,
      shouldHideParticipatingLocations: !!f.shouldHideParticipatingLocations,
      allowDuplicates: !!f.allowDuplicates,
      photoBannerUrl: this.editModel && this.editModel.photoBannerUrl,
      photoFullUrl: this.editModel && this.editModel.photoFullUrl,
      photoThumbUrl: this.photoUrl,
      uploadKey: this.uploadPhotoKey,
      uploadKeyBanner: this.uploadBannerKey,
      onboardingUrl: f.onboardingUrl,
      allowGiveStamps: !!f.allowGiveStamps,
      campaignStatus: f.campaignStatus,
      isGlobal: f.isGlobal,
      countries: ((f.countries || []) as any[]).map((country: any) => country?.iso2 ?? country),
      languages: ((f.languages || []) as any[]).map((l: any) => new AdvertiserLanguageDto({ code: l.code, name: l.name })),
      primaryLanguage: f.primaryLanguage,
      synchronizationContactGLO: f.synchronizationContactGLO,
      synchronizationDataConsumerGLO: f.synchronizationDataConsumerGLO,
      allowedCountryCodes: f.allowedCountryCodes
        ? (f.allowedCountryCodes as string).split(',').map((s: string) => s.trim().toUpperCase()).filter((s: string) => s.length > 0)
        : [],
    });
    if (model.startDate)
      model.startDate = new Date(Date.UTC(model.startDate.getFullYear(), model.startDate.getMonth(), model.startDate.getDate()));
    if (model.endDate)
      model.endDate = new Date(Date.UTC(model.endDate.getFullYear(), model.endDate.getMonth(), model.endDate.getDate()));

    return model;
  }

  forceValidatorsToDisplayErrors() {
    // force validators to display errors
    Object.keys(this.form.controls).forEach(key => {
      this.form.get(key).markAsTouched();
    });
  }

  onCampaignLanguagesChanged(languages: LanguageItem[]) {
    this.filteredLanguages = languages;
    this.availableLanguagesForPrimary = languages;
    const primaryLang = this.form.get('primaryLanguage')?.value;
    if (primaryLang && !languages.some(l => l.code === primaryLang)) {
      this.form.patchValue({ primaryLanguage: languages.length > 0 ? languages[0].code : '' });
    }
  }

  /**
   * Auto-populate countries and languages from advertiser when campaign has none (new campaign).
   */
  populateLanguagesFromAdvertiser() {
    if (!this.advertiser) return;

    // Auto-populate countries if empty
    const currentCountries = this.form.get('countries')?.value;
    if (!currentCountries || currentCountries.length === 0) {
      const advCountries = this.advertiser.countries || [];
      if (advCountries.length > 0) {
        this.allCountries.pipe(
          map(countries => countries.filter(c => advCountries.includes(c.iso2)))
        ).subscribe(result => {
          this.filteredCountries = result;
          this.form.patchValue({ countries: result } as any);
          setTimeout(() => this.refreshCountries.next(''));
        });
      }
    }

    // Auto-populate languages if empty
    const currentLanguages = this.form.get('languages')?.value;
    if (currentLanguages && currentLanguages.length > 0) return;

    const advLanguages: LanguageItem[] = (this.advertiser.languages || []).map(l => ({
      code: l.code!,
      name: l.name!,
    }));
    if (advLanguages.length === 0) return;

    this.filteredLanguages = advLanguages;
    this.availableLanguagesForPrimary = advLanguages;
    const primaryLang = this.advertiser.primaryLanguage || (advLanguages.length > 0 ? advLanguages[0].code : '');
    this.form.patchValue({ languages: advLanguages, primaryLanguage: primaryLang });
    setTimeout(() => this.refreshLanguages.next(''));
  }

  /**
   * Validates campaign countries/languages against advertiser's list.
   * Returns true if validation passes or user confirms override.
   */
  async validateLanguagesAgainstAdvertiser(): Promise<boolean> {
    if (!this.advertiser) return true;

    const f = this.form.value;
    const campaignCountries: string[] = ((f.countries || []) as unknown as any[]).map((c: any) => typeof c === 'string' ? c : c?.iso2).filter(Boolean);
    const campaignLanguages: string[] = ((f.languages || []) as unknown as any[]).map((l: any) => typeof l === 'string' ? l : l?.code).filter(Boolean);
    const advertiserCountries: string[] = this.advertiser.countries || [];
    const advertiserLanguages: string[] = (this.advertiser.languages || []).map(l => l.code!);

    const extraCountries = campaignCountries.filter(c => !advertiserCountries.includes(c));
    const extraLanguages = campaignLanguages.filter(l => !advertiserLanguages.includes(l));

    if (extraCountries.length === 0 && extraLanguages.length === 0) return true;

    const dialogRef = this.dialog.open(CampaignLanguageWarningDialogComponent, {
      data: { extraCountries, extraLanguages, advertiserId: this.advertiser.id }
    });

    const result = await lastValueFrom(dialogRef.afterClosed());
    return result === true;
  }

  async onSubmit() {
  }

  openPageBuilder(): void {
    const campaignId = this.campaign?.campaignId;

    if (!campaignId) {
      this.snackBar.open('Please finished campaign setup first', 'Close', { duration: 3000 });
      return;
    }

    this.campaignsApi.getCampaign(campaignId).subscribe({
      next: (result) => {
        if (!result?.campaignId) {
          this.snackBar.open('Please finished campaign setup first', 'Close', { duration: 3000 });
          return;
        }

        const advertiserId = this.campaign?.advertiserId;
        const campaignTitle = this.form.get('title')?.value || this.campaign?.title || '';
        const name = `Landing Page ${campaignTitle}`.trim();

        const params = new URLSearchParams({
          advertiserId: advertiserId ?? '',
          name,
          campaignId,
          campaignName: campaignTitle,
        });

        window.open(`${environment.contentManagementUrl}/landing-page/new?${params.toString()}`, '_blank');
      },
      error: () => {
        this.snackBar.open('Please finished campaign setup first', 'Close', { duration: 3000 });
      }
    });
  }


  async onBannerImageSelected(event) {
    try {
      const file = event.target.files[0];
      if (!file || !this.isValidBanner(file)) {
        this.snackBar.open('Invalid image file', 'Dismiss', { duration: 2000 });
        return;
      }

      this.uploadBannerKey = GUID.newGuid().replace(/-/g, '') + '.' + file.name.split('.').pop(); // preserve extension on s3 key
      const uploadUrl = await lastValueFrom(this.campaignsApi.getUploadUrl(this.uploadBannerKey));
      const uploadSuccess = await lastValueFrom(S3Upload.upload(uploadUrl, file));
      if (uploadSuccess) {
        const parts = uploadUrl.split('?');
        const photoBannerUrl = parts[0];
        this.photoBannerUrl = photoBannerUrl.replace('s3.ap-southeast-1.amazonaws.com/', ''); // trim s3 part to get photo using cloudfront


        // notify banner slider tab to its first banner
        this.mainBannerChanged.emit(new BannerImage({ photoUrl: this.photoBannerUrl, uploadKey: this.uploadBannerKey }));
      }
    } catch (err) {
      console.error('upload failed', err);
    }
  }

  async onThumbImageSelected(event) {
    try {
      const file = event.target.files[0];
      const {result, imageUrl, errorMessage } = await this.uploadImageService.uploadImage(this.uploadPhotoKey, file, null);

      if (!result) {
        this.snackBar.open(errorMessage, 'Dismiss', { duration: 2000 });
      } else {
        this.photoUrl = imageUrl;
      }
    } catch (err) {
      console.error('upload failed', err);
    }
  }

  isValidBanner(file: File): boolean {
    return file.name && this.ALLOWED_EXTENSIONS_BANNER.filter(e => file.name.trim().toLowerCase().endsWith(e)).length > 0;
  }

  get isInformationStrategy() {
    return this.campaign && this.campaign.strategy &&
      (this.campaign.strategy.name === 'ConsumerInformationStrategy' || this.campaign.strategy.name === 'StaffInformationStrategy');
  }

  public setMainBanner(banner: BannerImage) {
    this.photoBannerUrl = banner && banner.photoUrl ? banner.photoUrl : undefined;
    this.uploadBannerKey = banner && banner.uploadKey ? banner.uploadKey : undefined;
  }

  getEditorInstance(editorInstance) {
    QuillConfigurator.initEditor(editorInstance, {
      openLinkDialog: (initialTitle: string) => new Promise(resolve => {
        const dialogRef = this.dialog.open(CampaignDescriptionLinkComponent, {
          data: { title: initialTitle },
          width: '600px',
          height: '500px'
        });
        dialogRef.afterClosed().subscribe(resolve);
      })
    });
  }
}
